import React, { useState } from 'react';
import { translate } from 'react-i18next';
import {
  ActivityIndicator, Dimensions, Platform,
  StyleSheet, TouchableOpacity, View, Image, Text,
} from 'react-native';
import { connect } from 'react-redux';
import { Icon } from '../components';
import Theme from '../theme';
import { closePaywallModalAction } from '../payments/redux/payments-actions';
import { ActionPanel } from '../ui';
import { UserDataConsumer } from '../utils';
import { PaymentMethodButtons } from '../payments/components';
import WorkoutBackground from '../assets/images/welcome1-background.png';
import NutritionBackground from '../assets/images/nutrition-background.png';
import TrackingBackground from '../assets/images/tracking-background.png';
import { openPaymentRouter } from '../payments/utils/PaymentUtils';
import { PaywallPage, PaywallScreen } from './types';
import { getPaywallScreenSelector } from '../payments/redux/payments-selectors';
import PreLoadedPlanDetails from '../payments/PreLoadedPlanDetails';
import NoPlanIdForStudioSubscriptionPaywall from '../payments/components/NoPlanIdForStudioSubscriptionPaywall';
import { hasAccessToAStudioGymSelector } from '../auth/auth-selectors';
const backgroundImagesForPage = {
  [PaywallPage.Workout]: WorkoutBackground,
  [PaywallPage.Nutrition]: NutritionBackground,
  [PaywallPage.Tracking]: TrackingBackground,
};

const { width } = Dimensions.get('window');
const isMobileWeb = width <= 550 && Platform.OS === 'web';

const Paywall = (
  {
    t,
    page,
    stripeHomePlanSummary,
    user,
    paywallModalVisible,
    onCloseCallback,
    closePaywallModal,
    paywallScreen,
    userProfileService,
    onJoinSuccessCallBack,
    hasAccessToAStudioGym,
  }) => {
  const [loading, setLoading] = useState(false);

  const closeModalAndCallCallback = () => {
    if (onCloseCallback) {
      onCloseCallback();
    }
    closePaywallModal();
  };

  const openPaymentFlow = (paymentMode) => {
    setLoading(true);
    const coupon = '';
    const onceOffPriceIds = '';
    openPaymentRouter(paymentMode, onceOffPriceIds, user.userProfile.uid, coupon,
      stripeHomePlanSummary.summary.planId);
  };

  if (!paywallModalVisible) {
    return <></>;
  }

  const userSubscribedSuccess = async () => {
    await userProfileService.refreshCurrentUserProfile();
    closePaywallModal();
    if (onJoinSuccessCallBack) {
      await onJoinSuccessCallBack();
    }
  };

  const paywallScreenMap = {
    [PaywallScreen.InAppPurchase]: (
      <>
        {stripeHomePlanSummary.isLoading &&
          <ActivityIndicator
            size='small'
            color={Theme.PRIMARY}
            animating={true}
            style={styles.loading}
          />
        }
        {stripeHomePlanSummary.summary &&
          <>
            <Image style={styles.image} source={backgroundImagesForPage[page]} />
            <View style={{ paddingHorizontal: 20 }}>

              <View style={styles.paymentInfoContainer}>
                <Text style={styles.paymentInfo}>
                  ${stripeHomePlanSummary.summary.total}/{stripeHomePlanSummary.summary.interval}. {t('cancelAnytime')}
                </Text>
              </View>

              <Text style={[styles.paymentInfo, styles.experiencePersonalisation]}>
                {t('auth:experienceWorkoutNutritionTracking')}</Text>
              <PaymentMethodButtons
                t={t}
                showTitle={false}
                isLoading={loading}
                onPress={(paymentMode) => openPaymentFlow(paymentMode)}
                hasAccessToAStudioGym={hasAccessToAStudioGym}
              />
            </View>
          </>
        }
      </>
    ),
    [PaywallScreen.CardPayment]: (
      <PreLoadedPlanDetails modalMode onJoinSuccess={userSubscribedSuccess} />
    ),
    [PaywallScreen.SelectStudioPlan]: (
      <NoPlanIdForStudioSubscriptionPaywall userProfile={user.userProfile} hasAccessToAStudioGym={hasAccessToAStudioGym} />
    ),
  };

  return (
    <ActionPanel
      transparent
      visible
      animationType="fade"
      onRequestClose={closeModalAndCallCallback}
      mobileWebContainerStyle={StyleSheet.flatten(styles.container)}
      style={StyleSheet.flatten(styles.container)}>
      <View style={styles.body}>
        {paywallScreenMap[paywallScreen]}
      <TouchableOpacity
        style={styles.closeButton}
        hitSlop={{ bottom: 20, top: 20, left: 20, right: 20 }}
        onPress={closeModalAndCallCallback}>
        <Icon
          size={35}
          name={'md-close'}
          color={Theme.WHITE}
        />
      </TouchableOpacity>
      </View>
    </ActionPanel>
  );
};

const styles = StyleSheet.create({
  container: {
    height: '100%',
    borderRadius: 0,
    backgroundColor: Theme.GREY_DARK,
  },
  loading: {
    flex: 1,
  },
  body: {
    height: '100%',
    maxWidth: 640,
  },
  paymentInfoContainer: {
    paddingVertical: 10,
    borderBottomWidth: 0.5,
    borderBottomColor: Theme.GREY_DARK_MEDIUM,
  },
  paymentInfo: {
    fontSize: 18,
    textAlign: 'center',
    color: Theme.WHITE,
    fontFamily: Theme.Custom500,
  },
  experiencePersonalisation: {
    paddingVertical: 10,
  },
  image: {
    flex: 1,
    resizeMode: 'cover',
    height: '100%',
    width: '100%',
  },
  closeButton: {
    top: isMobileWeb ? 10 : 40,
    right: 0,
    marginRight: 20,
    position: 'absolute',
  },
});

const mapStateToProps = (state, ownProps) => {
  return {
    stripeHomePlanSummary: state.auth.signup.stripeHomePlanSummary,
    paywallModalVisible: state.payments.paywallModalVisible,
    onCloseCallback: state.payments.paywallOnCloseCallback,
    onJoinSuccessCallBack: state.payments.paywallOnSuccessCallback,
    page: state.payments.paywallPage,
    paywallScreen: getPaywallScreenSelector(state, ownProps.userProfile),
    hasAccessToAStudioGym: hasAccessToAStudioGymSelector(state),
  };
};

const mapDispatchToProps = (dispatch) => ({
  closePaywallModal: () => dispatch(closePaywallModalAction()),
});

export const PaywallModal = connect(mapStateToProps, mapDispatchToProps)(
  translate(['payments', 'auth'], { wait: true })(UserDataConsumer(Paywall)));
